class BeatStore {
    constructor() {
        this.currentAudio = null;
        this.currentBeatItem = null;
        this.playerBar = document.getElementById('beatPlayerBar');
        this.playPauseBtn = document.getElementById('playerPlayPause');
        this.playIcon = this.playPauseBtn.querySelector('.play-icon');
        this.pauseIcon = this.playPauseBtn.querySelector('.pause-icon');
        this.progressBar = document.querySelector('.progress-bar');
        this.progressFill = document.querySelector('.progress-fill');
        this.playerTime = document.querySelector('.player-time');
        this.playerTrackName = document.querySelector('.player-track-name');
        this.progressTooltip = document.getElementById('progressTooltip');
        this.playerCartBtn = document.getElementById('playerCartBtn');
        this.playerCartPrice = document.getElementById('playerCartPrice');
        this.licenseModal = document.getElementById('licenseModal');
        this.currentProductData = null;
        this.selectedLicense = null;

        this.volumeBtn = document.getElementById('playerVolumeBtn');
        this.volumeSliderTrack = document.getElementById('volumeSliderTrack');
        this.volumeSliderFill = document.getElementById('volumeSliderFill');
        
        this.volumeIcons = {
            normal: document.getElementById('volume-icon-normal'),
            mute: document.getElementById('volume-icon-mute')
        };
        
        this.lastVolume = 50; 
        this.isDraggingVolume = false; 
        this.isMuted = false; 
        this.isMobile = window.innerWidth <= 768; 
        this.isDraggingProgress = false;
        this.wasPlayingBeforeDrag = false; 

        this.beatsPerLoad = parseInt(document.getElementById('beats-per-load-config')?.dataset.beatsPerLoad || 50);
        this.currentlyDisplayedBeats = 0;
        
        this.allBeatsData = [];
        this.filteredBeatsData = [];
        this.renderedElements = new Map();
        
        this.loadMoreContainer = null;
        this.loadMoreBtn = null;
        this.loadAllBtn = null;

        this.currentSort = { column: 'date', direction: 'desc' };
        this.currentPlayingHandle = null;

        this.autoplayEnabled = true;
        this.seekStep = 5;
        this.volumeSliderTimeout = null;

        this.init();
        this.initVolumeControls();
        this.initKeyboardControls();
        this.initScrollVolumeControl(); 
        this.initLoadMoreSystem();
    }

    isMobileDevice() {
        return window.innerWidth <= 768;
    }

    getCorrectDisplayValue() {
        return this.isMobileDevice() ? 'flex' : 'grid';
    }

    init() {
        this.setupPlayerListeners();
        this.setupFilters();
        this.setupSearch();
        this.setupAnimations();
        this.setupSearchClear();
    }

    createBeatElement(data) {
        if (this.renderedElements.has(data.handle)) {
            return this.renderedElements.get(data.handle);
        }

        const config = window.beatStoreConfig || {};
        const div = document.createElement('div');
        div.className = 'beat-item';
        div.dataset.productId = data.handle;
        div.dataset.variantId = data.variantId;
        div.dataset.category = data.category;
        div.dataset.bpm = data.bpm;
        div.dataset.price = data.price;
        div.dataset.tags = JSON.stringify(data.tags);
        div.dataset.img = data.img || '';
        div.dataset.title = data.title;
        div.dataset.createdDate = data.createdDate;

        let tagsHtml = '&nbsp;';
        if (data.tags && data.tags.length > 0) {
            tagsHtml = data.tags
                .filter(t => t && t.trim())
                .map(t => `<span class="beat-tag" data-tag-value="${this.escapeHtml(t)}">${this.escapeHtml(t)}</span>`)
                .join('');
            if (!tagsHtml) tagsHtml = '&nbsp;';
        }

        let actionsHtml = '';
        if (config.showDownloadButton) {
            actionsHtml += `<button class="beat-download" aria-label="${this.escapeHtml(config.ariaDownload || 'Download beat')}" data-product-handle="${this.escapeHtml(data.handle)}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 1v12m0 0l4-4m-4 4l-4-4M1 14v4a1 1 0 001 1h16a1 1 0 001-1v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>`;
        }
        if (config.showShareButton) {
            actionsHtml += `<button class="beat-share" aria-label="${this.escapeHtml(config.ariaShare || 'Share beat')}" data-url="${this.escapeHtml(data.productUrl)}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="5" cy="10" r="3" stroke="currentColor" stroke-width="2"/>
                    <circle cx="15" cy="5" r="3" stroke="currentColor" stroke-width="2"/>
                    <circle cx="15" cy="15" r="3" stroke="currentColor" stroke-width="2"/>
                    <path d="M7.5 8.5l5-3M7.5 11.5l5 3" stroke="currentColor" stroke-width="2"/>
                </svg>
            </button>`;
        }

        div.innerHTML = `
            <div class="beat-cover">
                ${data.imgThumb 
                    ? `<img src="${this.escapeHtml(data.imgThumb)}" alt="${this.escapeHtml(data.title)}" loading="lazy" decoding="async" width="50" height="50">` 
                    : '<div class="beat-cover-placeholder"></div>'}
            </div>
            <div class="beat-info">
                <h3 class="beat-title">${this.escapeHtml(data.title)}</h3>
                ${data.audioUrl 
                    ? `<audio class="beat-audio" preload="none" data-src="${this.escapeHtml(data.audioUrl)}"><source src="${this.escapeHtml(data.audioUrl)}" type="audio/mpeg"></audio>` 
                    : ''}
            </div>
            <div class="beat-time">${this.escapeHtml(data.duration)}</div>
            <div class="beat-bpm">${data.bpm}</div>
            <div class="beat-tags">${tagsHtml}</div>
            <div class="beat-date">${this.escapeHtml(data.dateFormatted)}</div>
            <div class="beat-actions">
                ${actionsHtml}
                <button class="beat-cart-btn" data-price="${this.escapeHtml(data.priceFormatted)}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-shopping-bag"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                    <span>${this.escapeHtml(data.priceFormatted)}</span>
                </button>
            </div>`;

        this.attachBeatEventListeners(div);

        if (this.currentPlayingHandle === data.handle) {
            div.classList.add('playing');
            this.currentBeatItem = div;
        }

        this.renderedElements.set(data.handle, div);
        return div;
    }

    escapeHtml(str) {
        if (str == null) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    initLoadMoreSystem() {
        const dataScript = document.getElementById('beat-store-data');
        if (dataScript) {
            try {
                this.allBeatsData = JSON.parse(dataScript.textContent);
            } catch (e) {
                this.allBeatsData = [];
            }
        }

        this.currentlyDisplayedBeats = 0;

        if (window.beatStoreConfig && window.beatStoreConfig.needsAjaxLoad) {
            this.loadRemainingBeatsViaAjax().then(() => {
                this.sortAndDisplayAllBeats();
            });
        } else {
            this.sortAndDisplayAllBeats();
        }
    }

    async loadRemainingBeatsViaAjax() {
        const collectionHandle = window.beatStoreConfig.collectionHandle;
        const totalProducts = window.beatStoreConfig.totalProducts;
        const productsPerPage = 250;
        const totalPages = Math.ceil(totalProducts / productsPerPage);

        for (let page = 2; page <= totalPages; page++) {
            try {
                const response = await fetch(`/collections/${collectionHandle}?page=${page}`);
                const html = await response.text();

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                
                const remoteDataScript = doc.getElementById('beat-store-data');
                if (remoteDataScript) {
                    try {
                        const remoteData = JSON.parse(remoteDataScript.textContent);
                        this.allBeatsData = this.allBeatsData.concat(remoteData);
                    } catch (e) {
                        console.error(`Error parsing JSON from page ${page}:`, e);
                    }
                }
            } catch (error) {
                console.error(`Error loading page ${page}:`, error);
            }
        }
    }

    sortAndDisplayAllBeats() {
        this.filteredBeatsData = [...this.allBeatsData];

        this.filteredBeatsData.sort((a, b) => parseInt(b.createdDate || 0) - parseInt(a.createdDate || 0));
        this.currentSort = { column: 'date', direction: 'desc' };

        this.populateTagsDropdown();

        this.loadMoreContainer = document.querySelector('.beat-load-more-container');
        this.loadMoreBtn = document.querySelector('.beat-load-more-btn');
        this.loadAllBtn = document.querySelector('.beat-load-all-btn');

        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener('click', () => this.loadMoreBeats());
        }
        if (this.loadAllBtn) {
            this.loadAllBtn.addEventListener('click', () => this.loadAllBeats());
        }

        this.renderVisibleBeats();

        const beatListEl = document.querySelector('.beat-list');
        if (beatListEl) beatListEl.style.minHeight = '';

        requestAnimationFrame(() => {
            this.setupColumnSort();
        });
    }

    populateTagsDropdown() {
        const tagsSelect = document.querySelector('[data-filter="tags"]');
        if (!tagsSelect) return;

        const allTags = new Set();
        this.allBeatsData.forEach(beat => {
            if (beat.tags && Array.isArray(beat.tags)) {
                beat.tags.forEach(tag => {
                    if (tag && tag.trim()) allTags.add(tag.trim());
                });
            }
        });

        const sorted = [...allTags].sort((a, b) => a.localeCompare(b));
        sorted.forEach(tag => {
            const option = document.createElement('option');
            option.value = tag;
            option.textContent = tag;
            tagsSelect.appendChild(option);
        });
    }

    renderVisibleBeats() {
        const beatList = document.querySelector('.beat-list');
        if (!beatList) return;

        const newCount = Math.min(
            this.currentlyDisplayedBeats + this.beatsPerLoad,
            this.filteredBeatsData.length
        );

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < newCount; i++) {
            const el = this.createBeatElement(this.filteredBeatsData[i]);
            fragment.appendChild(el);
        }

        beatList.replaceChildren(fragment);

        this.currentlyDisplayedBeats = newCount;
        this.updateLoadMoreButton();
        this.triggerBeatAnimations();
    }

    loadMoreBeats() {
        if (!this.loadMoreBtn) return;
        this.loadMoreBtn.classList.add('loading');

        setTimeout(() => {
            this.renderVisibleBeats();
            this.loadMoreBtn.classList.remove('loading');
        }, 200);
    }

    loadAllBeats() {
        if (!this.loadAllBtn) return;
        this.loadAllBtn.classList.add('loading');

        setTimeout(() => {
            this.currentlyDisplayedBeats = this.filteredBeatsData.length - this.beatsPerLoad;
            this.renderVisibleBeats();
            this.loadAllBtn.classList.remove('loading');
        }, 200);
    }

    updateLoadMoreButton() {
        if (!this.loadMoreContainer) return;
        
        const hasMoreBeats = this.currentlyDisplayedBeats < this.filteredBeatsData.length;
        
        if (hasMoreBeats) {
            this.loadMoreContainer.style.display = 'flex';
            setTimeout(() => {
                this.loadMoreContainer.classList.add('is-visible');
            }, 100);
        } else {
            this.loadMoreContainer.style.display = 'none';
            this.loadMoreContainer.classList.remove('is-visible');
        }
    }

    triggerBeatAnimations() {
        const beatList = document.querySelector('.beat-list');
        if (!beatList) return;

        const items = beatList.querySelectorAll('.beat-item:not(.is-visible)');
        items.forEach((beat, index) => {
            setTimeout(() => {
                beat.classList.add('is-visible');
            }, index * 30);
        });
    }
    
    setupAnimations() {
        const animatedElements = [
            document.querySelector('.beat-store-heading'),
            document.querySelector('.beat-collection-description'),
            document.querySelector('.beat-filters'),
            document.querySelector('.beat-search-container'),
            document.querySelector('.beat-list-header'),
            document.querySelector('.pricing-plans .section-title'),
            ...document.querySelectorAll('.plan-card')
        ];

        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, {
            rootMargin: '0px 0px -20px 0px', 
            threshold: 0.05 
        });

        let delayIndex = 0;
        animatedElements.forEach((el) => {
            if (!el) return;
            if (el.classList.contains('is-visible')) {
                observer.unobserve(el);
                return;
            }
            el.style.transitionDelay = `${delayIndex * 50}ms`;
            observer.observe(el);
            delayIndex++;
        });
    }

    attachBeatEventListeners(beatItem) {
        beatItem.addEventListener('click', (e) => {
            const targetActionElement = e.target.closest('.beat-actions button');
            if (targetActionElement) return;
            this.playBeat(beatItem);
        });

        const cartBtn = beatItem.querySelector('.beat-cart-btn');
        if (cartBtn) {
            cartBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const productHandle = beatItem.dataset.productId;
                if (productHandle) {
                    window.location.href = `/products/${productHandle}`;
                } else {
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }

        const downloadBtn = beatItem.querySelector('.beat-download');
        if (downloadBtn) {
            downloadBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleDownload(beatItem);
            });
        }

        const shareBtn = beatItem.querySelector('.beat-share');
        if (shareBtn) {
            shareBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const shareUrl = shareBtn.dataset.url;
                if (navigator.share) {
                    try {
                        await navigator.share({ title: document.title, url: shareUrl });
                    } catch (error) {
                        console.error('Share failed:', error);
                    }
                } else {
                    navigator.clipboard.writeText(shareUrl).then(() => {
                        this.showNotification('Lien copié dans le presse-papier!', 'success');
                    });
                }
            });
        }

        beatItem.querySelectorAll('.beat-tag').forEach(tag => {
            tag.addEventListener('click', (e) => {
                e.stopPropagation();
                const tagText = tag.getAttribute('data-tag-value');
                const searchInput = document.querySelector('.beat-search-input');
                
                searchInput.value = tagText;
                this.searchBeats(tagText);
                this.updateSearchClearVisibility();
                
                searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                searchInput.focus();
                
                document.querySelectorAll('.beat-filter-select').forEach(filter => {
                    filter.value = filter.querySelector('option').value;
                });
            });
        });
    }

    setupPlayerListeners() {
        this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
        document.getElementById('playerPrev').addEventListener('click', () => this.playPrevious());
        document.getElementById('playerNext').addEventListener('click', () => this.playNext());
        document.getElementById('playerClose').addEventListener('click', () => this.closePlayer());

        this.setupProgressBarDrag();

        if (this.progressBar) {
            this.progressBar.addEventListener('mousemove', (e) => this.updateTooltip(e));
            this.progressBar.addEventListener('mouseleave', () => this.hideTooltip());
        }

        if (this.playerCartBtn) {
            this.playerCartBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.currentProductData && this.currentProductData.productHandle) {
                    window.location.href = `/products/${this.currentProductData.productHandle}`;
                } else {
                    this.showNotification('Impossible de charger les informations du produit.', 'error');
                }
            });
        }
    }

    setupFilters() {
        document.querySelectorAll('.beat-filter-select').forEach(filter => {
            filter.addEventListener('change', () => this.applyFilters());
        });
    }

    setupProgressBarDrag() {
        if (!this.progressBar) return;

        const setProgressFromEvent = (e) => {
            if (!this.currentAudio || !this.currentAudio.duration) return;
            const rect = this.progressBar.getBoundingClientRect();
            const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            this.currentAudio.currentTime = percent * this.currentAudio.duration;
        };

        const startDrag = (e) => {
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            if (this.wasPlayingBeforeDrag) this.currentAudio.pause();
            this.isDraggingProgress = true;
            setProgressFromEvent(e);
            this.progressBar.classList.add('dragging');
            e.preventDefault();
        };

        const moveDrag = (e) => {
            if (this.isDraggingProgress) {
                setProgressFromEvent(e);
                this.updateTooltip(e);
            }
        };

        const endDrag = () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        };

        this.progressBar.addEventListener('click', (e) => {
            if (!this.isDraggingProgress) setProgressFromEvent(e);
        });

        this.progressBar.addEventListener('mousedown', startDrag);
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('mouseup', endDrag);

        this.progressBar.addEventListener('touchstart', (e) => {
            this.wasPlayingBeforeDrag = this.currentAudio && !this.currentAudio.paused;
            if (this.wasPlayingBeforeDrag) this.currentAudio.pause();
            this.isDraggingProgress = true;
            this.progressBar.classList.add('dragging');
            const touch = e.touches[0];
            setProgressFromEvent(touch);
            e.preventDefault();
        }, { passive: false });

        document.addEventListener('touchmove', (e) => {
            if (this.isDraggingProgress && e.touches.length > 0) {
                setProgressFromEvent(e.touches[0]);
            }
        }, { passive: false });

        document.addEventListener('touchend', () => {
            if (this.isDraggingProgress) {
                this.isDraggingProgress = false;
                this.progressBar.classList.remove('dragging');
                if (this.wasPlayingBeforeDrag && this.currentAudio) {
                    this.currentAudio.play();
                    this.wasPlayingBeforeDrag = false;
                }
            }
        });
    }

    setupSearch() {
        const searchInput = document.querySelector('.beat-search-input');
        let searchTimeout;
        searchInput.addEventListener('input', (e) => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => this.searchBeats(e.target.value), 300);
            this.updateSearchClearVisibility();
        });
    }

    setupSearchClear() {
        const searchInput = document.querySelector('.beat-search-input');
        const clearButton = document.querySelector('.beat-search-clear');
        
        if (!clearButton || !searchInput) return;

        clearButton.addEventListener('click', () => {
            searchInput.value = '';
            this.searchBeats('');
            this.updateSearchClearVisibility();
            searchInput.focus();
        });

        this.updateSearchClearVisibility();
    }
    
    updateSearchClearVisibility() {
        const searchInput = document.querySelector('.beat-search-input');
        const clearButton = document.querySelector('.beat-search-clear');
        if (!clearButton || !searchInput) return;
        
        if (searchInput.value.length > 0) {
            clearButton.classList.add('show');
        } else {
            clearButton.classList.remove('show');
        }
    }

    setupColumnSort() {
        const headers = document.querySelectorAll('.beat-header-sortable');
        
        if (headers.length === 0) {
            setTimeout(() => this.setupColumnSort(), 100);
            return;
        }
        
        headers.forEach(header => {
            header.addEventListener('click', () => {
                const column = header.dataset.sort;
                if (this.currentSort.column === column) {
                    this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
                } else {
                    this.currentSort.column = column;
                    this.currentSort.direction = 'asc';
                }
                this.updateSortIcons();
                this.sortBeats();
            });
        });
    }

    updateSortIcons() {
        document.querySelectorAll('.beat-header-sortable').forEach(header => {
            const icon = header.querySelector('.sort-icon');
            if (!icon) return;
            
            if (header.dataset.sort === this.currentSort.column) {
                header.classList.add('sorted');
                icon.style.transform = this.currentSort.direction === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)';
            } else {
                header.classList.remove('sorted');
                icon.style.transform = 'rotate(0deg)';
            }
        });
    }

    sortBeats() {
        this.filteredBeatsData.sort((a, b) => {
            let valA, valB;
            switch(this.currentSort.column) {
                case 'title':
                    valA = (a.title || '').toLowerCase();
                    valB = (b.title || '').toLowerCase();
                    return this.currentSort.direction === 'asc' 
                        ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'duration':
                    valA = this.parseDurationToSeconds(a.duration);
                    valB = this.parseDurationToSeconds(b.duration);
                    return this.currentSort.direction === 'asc' ? valA - valB : valB - valA;
                case 'bpm':
                    valA = parseInt(a.bpm || 0);
                    valB = parseInt(b.bpm || 0);
                    return this.currentSort.direction === 'asc' ? valA - valB : valB - valA;
                case 'date':
                default:
                    valA = parseInt(a.createdDate || 0);
                    valB = parseInt(b.createdDate || 0);
                    return this.currentSort.direction === 'asc' ? valA - valB : valB - valA;
            }
        });

        this.currentlyDisplayedBeats = 0;
        this.renderVisibleBeats();
    }

    parseDurationToSeconds(duration) {
        if (!duration) return 0;
        const parts = String(duration).split(':');
        if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
        return parseInt(duration) || 0;
    }

    applyFilters() {
        const filters = {
            bpm: document.querySelector('[data-filter="bpm"]').value,
            tags: document.querySelector('[data-filter="tags"]').value,
            sort: document.querySelector('[data-filter="sort"]').value
        };

        let visibleBeats = this.allBeatsData.filter(item => {
            const itemBpm = parseInt(item.bpm);

            let bpmMatch = filters.bpm === 'all';
            if (!bpmMatch) {
                const bpmSelect = document.querySelector('[data-filter="bpm"]');
                const selectedOption = bpmSelect.querySelector(`option[value="${filters.bpm}"]`);
                if (selectedOption) {
                    const minBpm = parseInt(selectedOption.dataset.min || 0);
                    const maxBpm = parseInt(selectedOption.dataset.max || 999);
                    bpmMatch = itemBpm >= minBpm && itemBpm <= maxBpm;
                }
            }

            let tagsMatch = filters.tags === 'all' || 
                (item.tags && item.tags.includes(filters.tags));

            return bpmMatch && tagsMatch;
        });

        if (filters.sort === 'date-desc') {
            visibleBeats.sort((a, b) => parseInt(b.createdDate || 0) - parseInt(a.createdDate || 0));
        } else if (filters.sort === 'date-asc') {
            visibleBeats.sort((a, b) => parseInt(a.createdDate || 0) - parseInt(b.createdDate || 0));
        } else {
            visibleBeats.sort((a, b) => parseInt(b.createdDate || 0) - parseInt(a.createdDate || 0));
        }

        this.filteredBeatsData = visibleBeats;
        this.currentlyDisplayedBeats = 0;
        this.renderVisibleBeats();
    }

    searchBeats(query) {
        const searchTerm = query.toLowerCase().trim();

        let matchingBeats = this.allBeatsData;

        if (searchTerm) {
            matchingBeats = this.allBeatsData.filter(item => {
                const title = (item.title || '').toLowerCase();
                const tags = (item.tags || []).map(t => t.toLowerCase());
                const bpm = String(item.bpm || '');

                return title.includes(searchTerm) || 
                    tags.some(tag => tag.includes(searchTerm)) ||
                    bpm.includes(searchTerm);
            });
        }

        matchingBeats.sort((a, b) => parseInt(b.createdDate || 0) - parseInt(a.createdDate || 0));

        this.filteredBeatsData = matchingBeats;
        this.currentlyDisplayedBeats = 0;
        this.renderVisibleBeats();
    }

    async playBeat(beatItem) {
        const audioElement = beatItem.querySelector('.beat-audio');
        if (!audioElement) return;

        const audioUrl = audioElement.dataset.src || audioElement.querySelector('source')?.src;
        if (!audioUrl) return;

        if (this.currentBeatItem === beatItem && this.currentAudio && !this.currentAudio.paused) {
            this.togglePlayPause();
            return;
        }

        if (this.currentAudio) {
            this.lastVolume = Math.round(this.currentAudio.volume * 100);
            this.currentAudio.pause();
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }

        const volumeToUse = this.lastVolume;
        this.currentAudio = new Audio(audioUrl);
        this.currentAudio.volume = volumeToUse / 100;
        this.currentBeatItem = beatItem;
        this.currentPlayingHandle = beatItem.dataset.productId;

        this.currentAudio.addEventListener('timeupdate', () => this.updateProgress());
        this.currentAudio.addEventListener('loadedmetadata', () => this.updateTimeDisplay());
        this.currentAudio.addEventListener('ended', () => {
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
            if (this.autoplayEnabled) {
                this.playNext();
            }
        });

        this.currentAudio.addEventListener('volumechange', () => {
            if (!this.isDraggingVolume) {
                const newVolumePercent = Math.round(this.currentAudio.volume * 100);
                this.updateVolumeIcon(newVolumePercent);
                if (this.volumeSliderFill) {
                    this.volumeSliderFill.style.height = `${newVolumePercent}%`;
                }
                if (newVolumePercent > 0) {
                    this.lastVolume = newVolumePercent;
                }
            }
        });

        this.updateVolumeIcon(volumeToUse);
        if (this.volumeSliderFill) {
            this.volumeSliderFill.style.height = `${volumeToUse}%`;
        }

        this.updatePlayerUI(beatItem);
        beatItem.classList.add('playing');
        this.showPlayer();

        try {
            await this.currentAudio.play();
            this.updatePlayPauseButton(true);
        } catch (error) {
            console.error('Play error:', error);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }

    showPlayer() {
        this.playerBar.style.display = 'block';
        setTimeout(() => {
            this.playerBar.classList.add('active');
        }, 10);
    }

    closePlayer() {
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio = null;
        }

        if (this.currentBeatItem) {
            this.currentBeatItem.classList.remove('playing');
            this.currentBeatItem = null;
        }
        this.currentPlayingHandle = null;

        this.playerBar.classList.remove('active');
        setTimeout(() => {
            this.playerBar.style.display = 'none';
        }, 300);
    }

    togglePlayPause() {
        if (!this.currentAudio) return;

        if (this.currentAudio.paused) {
            this.currentAudio.play().then(() => {
                this.updatePlayPauseButton(true);
                if (this.currentBeatItem) {
                    this.currentBeatItem.classList.add('playing');
                }
            });
        } else {
            this.currentAudio.pause();
            this.updatePlayPauseButton(false);
            if (this.currentBeatItem) {
                this.currentBeatItem.classList.remove('playing');
            }
        }
    }

    playNext() {
        if (!this.currentPlayingHandle || this.filteredBeatsData.length === 0) return;

        const currentIndex = this.filteredBeatsData.findIndex(d => d.handle === this.currentPlayingHandle);
        if (currentIndex < 0 || currentIndex + 1 >= this.filteredBeatsData.length) return;
        
        const nextData = this.filteredBeatsData[currentIndex + 1];
        const nextIndex = currentIndex + 1;

        if (nextIndex >= this.currentlyDisplayedBeats) {
            this.currentlyDisplayedBeats = nextIndex;
            this.renderVisibleBeats();
        }

        const nextEl = this.renderedElements.get(nextData.handle);
        if (nextEl) {
            this.playBeat(nextEl);
            this.scrollToBeat(nextEl);
        }
    }

    playPrevious() {
        if (!this.currentPlayingHandle || this.filteredBeatsData.length === 0) return;

        const currentIndex = this.filteredBeatsData.findIndex(d => d.handle === this.currentPlayingHandle);
        if (currentIndex <= 0) return;
        
        const prevData = this.filteredBeatsData[currentIndex - 1];
        const prevEl = this.renderedElements.get(prevData.handle);
        if (prevEl) {
            this.playBeat(prevEl);
            this.scrollToBeat(prevEl);
        }
    }

    scrollToBeat(beatElement) {
        if (!beatElement) return;
        
        setTimeout(() => {
            const rect = beatElement.getBoundingClientRect();
            const playerHeight = this.playerBar?.offsetHeight || 80;
            const beatHeight = beatElement.offsetHeight || 60;
            
            const topMargin = beatHeight;
            const bottomMargin = playerHeight + beatHeight + 20;
            
            const isVisible = rect.top >= topMargin && rect.bottom <= (window.innerHeight - bottomMargin);
            
            if (!isVisible) {
                beatElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 50);
    }

    updatePlayPauseButton(isPlaying) {
        this.playIcon.style.display = isPlaying ? 'none' : 'block';
        this.pauseIcon.style.display = isPlaying ? 'block' : 'none';
    }

    updateProgress() {
        if (!this.currentAudio || !this.currentAudio.duration) return;

        const percent = (this.currentAudio.currentTime / this.currentAudio.duration) * 100;
        const customColor = getComputedStyle(this.progressFill).backgroundColor || '#000000';

        this.progressFill.style.cssText = `
            width: ${percent}% !important;
            background-color: ${customColor} !important;
            opacity: 1 !important;
            visibility: visible !important;
            display: block !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            height: 100% !important;
            z-index: 2147483647 !important;
        `;

        this.updateTimeDisplay();
    }

    updateTimeDisplay() {
        if (!this.currentAudio) {
            this.playerTime.textContent = "0:00 / 0:00";
            return;
        }
        const current = this.formatTime(this.currentAudio.currentTime);
        const duration = this.formatTime(this.currentAudio.duration || 0);
        this.playerTime.textContent = `${current} / ${duration}`;
    }

    formatTime(seconds) {
        if (isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    }

    updatePlayerUI(beatItem) {
        const title = beatItem.querySelector('.beat-title').textContent;
        const coverImgSrc = beatItem.querySelector('.beat-cover img')?.src;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const productHandle = beatItem.dataset.productId;

        this.playerTrackName.textContent = title;

        const playerAlbumCover = document.getElementById('playerAlbumCover');
        if (coverImgSrc) {
            playerAlbumCover.innerHTML = `<img src="${coverImgSrc}" alt="${title}">`;
        } else {
            playerAlbumCover.innerHTML = '<div class="player-album-cover-placeholder"></div>';
        }

        if (this.playerCartBtn && price) {
            if (this.playerCartPrice) {
                this.playerCartPrice.textContent = price;
            }
            this.playerCartBtn.style.display = 'flex';
        }

        this.currentProductData = {
            productHandle: productHandle,
            title: title,
            coverImgSrc: coverImgSrc,
            price: price
        };
    }
    
    updateTooltip(e) {
        if (!this.currentAudio || !this.currentAudio.duration || !this.progressTooltip) return;
    
        const rect = this.progressBar.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        const hoverTime = percent * this.currentAudio.duration;
    
        this.progressTooltip.textContent = this.formatTime(hoverTime);
        this.progressTooltip.style.left = `${x}px`;
        this.progressTooltip.style.opacity = '1';
        
        if (this.playerCartBtn) {
            const computedStyle = window.getComputedStyle(this.playerCartBtn);
            this.progressTooltip.style.backgroundColor = computedStyle.backgroundColor;
            this.progressTooltip.style.color = computedStyle.color;
        }
    }

    hideTooltip() {
        if (!this.progressTooltip) return;
        this.progressTooltip.style.opacity = '0';
    }

    async showLicenseModal(beatItem) {
        const productHandle = beatItem.dataset.productId;
        const variantId = beatItem.dataset.variantId;
        const title = beatItem.querySelector('.beat-title').textContent;
        const price = beatItem.querySelector('.beat-cart-btn span')?.textContent;
        const coverImg = beatItem.querySelector('.beat-cover img');

        if (!productHandle) {
            this.showNotification('Erreur: Produit non trouvé', 'error');
            return;
        }

        this.licenseModal.style.display = 'block';
        document.body.style.overflow = 'hidden';

        const modalProductInfo = document.getElementById('modalProductInfo');
        modalProductInfo.innerHTML = `
            <div style="display: flex; align-items: center; gap: 15px;">
                ${coverImg ? `<img src="${coverImg.src}" alt="${title}" style="width: 60px; height: 60px; border-radius: 8px; object-fit: cover;">` : '<div style="width: 60px; height: 60px; background: #333; border-radius: 8px;"></div>'}
                <div>
                    <h3 style="margin: 0 0 5px 0; color: #fff; font-size: 18px;">${title}</h3>
                    <p style="margin: 0; color: #888; font-size: 14px;">Prix de base: ${price}</p>
                </div>
            </div>
        `;

        try {
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();

            const licenseOptions = document.getElementById('licenseOptions');
            licenseOptions.innerHTML = '';

            product.variants.forEach(variant => {
                const option = document.createElement('div');
                option.className = 'license-option';
                option.dataset.variantId = variant.id;

                const licenseName = variant.title || 'Licence Standard';
                const licensePrice = new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(variant.price / 100);

                option.innerHTML = `
                    <div class="license-option-header">
                        <span class="license-option-name">${licenseName}</span>
                        <span class="license-option-price">${licensePrice}</span>
                    </div>
                    <div class="license-option-description">
                        ${this.getLicenseDescription(licenseName)}
                    </div>
                    ${!variant.available ? '<p class="license-option-unavailable">Non disponible</p>' : ''}
                `;

                if (!variant.available) option.classList.add('disabled');

                option.addEventListener('click', () => {
                    if (variant.available) {
                        document.querySelectorAll('.license-option').forEach(opt => opt.classList.remove('selected'));
                        option.classList.add('selected');
                        this.selectedLicense = variant;
                    }
                });

                licenseOptions.appendChild(option);
            });

            const addButton = document.createElement('button');
            addButton.className = 'license-add-btn';
            addButton.textContent = 'Ajouter au panier';
            addButton.addEventListener('click', () => this.addToCart());
            licenseOptions.appendChild(addButton);

        } catch (error) {
            console.error('Erreur chargement produit:', error);
            document.getElementById('licenseOptions').innerHTML = '<p class="error-message">Erreur lors du chargement des licences</p>';
        }

        const closeModal = document.getElementById('closeModal');
        const modalOverlay = this.licenseModal.querySelector('.license-modal-overlay');

        const closeHandler = () => {
            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;
        };

        closeModal.onclick = closeHandler;
        modalOverlay.onclick = closeHandler;
    }

    getLicenseDescription(licenseName) {
        const descriptions = {
            'Basic': 'Usage personnel uniquement, pas de revente',
            'Premium': 'Usage commercial, jusqu\'à 10,000 copies',
            'Exclusive': 'Droits exclusifs, le beat sera retiré de la vente',
            'Standard': 'Licence d\'utilisation musicale'
        };

        for (const [key, desc] of Object.entries(descriptions)) {
            if (licenseName.toLowerCase().includes(key.toLowerCase())) return desc;
        }
        return 'Licence d\'utilisation musicale';
    }

    async addToCart() {
        if (!this.selectedLicense) {
            alert('Veuillez sélectionner une licence');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('items[0][id]', this.selectedLicense.id);
            formData.append('items[0][quantity]', '1');
            formData.append('sections', 'cart-notification-product,cart-notification-button,cart-icon-bubble');

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) throw new Error('Erreur lors de l\'ajout au panier');

            const data = await response.json();

            this.licenseModal.style.display = 'none';
            document.body.style.overflow = '';
            this.selectedLicense = null;

            this.showShopifyNotification(data);

        } catch (error) {
            console.error('Erreur:', error);
            this.showNotification('Erreur lors de l\'ajout au panier', 'error');
        }
    }

    showShopifyNotification(data) {
        const cartNotification = document.querySelector('cart-notification');

        if (cartNotification && cartNotification.renderContents) {
            const parsedState = {
                key: data.items[0].key,
                sections: {}
            };
            if (data.sections) parsedState.sections = data.sections;
            cartNotification.renderContents(parsedState);
        } else {
            const event = new CustomEvent('cart:add', {
                detail: { items: data.items, sections: data.sections }
            });
            document.dispatchEvent(event);

            if (data.sections && data.sections['cart-icon-bubble']) {
                this.updateCartBubble(data.sections['cart-icon-bubble']);
            }
            const productTitle = data.items?.[0]?.product_title || 'Produit';
            this.showNotification(`"${productTitle}" ajouté au panier!`, 'success');
        }
    }

    updateCartBubble(bubbleHtml) {
        if (!bubbleHtml) return;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = bubbleHtml;
        const newBubble = tempDiv.querySelector('.cart-count-bubble, .cart-icon-bubble');

        const existingBubbles = document.querySelectorAll('.cart-count-bubble, .cart-icon-bubble');
        existingBubbles.forEach(bubble => {
            if (newBubble && bubble.parentNode) {
                bubble.parentNode.replaceChild(newBubble.cloneNode(true), bubble);
            }
        });
    }

    showNotification(message, type = 'success') {
        const notification = document.createElement('div');
        notification.className = `beat-notification beat-notification--${type}`;
        notification.textContent = message;
        document.body.appendChild(notification);

        setTimeout(() => notification.classList.add('show'), 10);

        setTimeout(() => {
            notification.classList.remove('show');
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    }

    handleDownload(beatItem) {
        const popupConfig = document.getElementById('popup-free-download-config');
        if (!popupConfig) {
            this.showNotification('Configuration du popup non trouvée', 'error');
            return;
        }

        const title = beatItem.dataset.title || beatItem.querySelector('.beat-title').textContent;
        const coverImg = beatItem.dataset.img || beatItem.querySelector('.beat-cover img')?.src;
        const audioEl = beatItem.querySelector('.beat-audio');
        const audioUrl = audioEl ? audioEl.dataset.src : '';

        if (!audioUrl) {
            this.showNotification('Pas de fichier audio disponible pour le téléchargement', 'error');
            return;
        }

        this.showDownloadPopup({
            title: popupConfig.dataset.popupTitle + ' : ' + title,
            subtitle: popupConfig.dataset.popupSubtitle,
            buttonText: popupConfig.dataset.popupButton,
            coverImg: coverImg,
            beatTitle: title,
            audioUrl: audioUrl
        });
    }

    showDownloadPopup(config) {
        const existingPopup = document.getElementById('free-download-popup');
        if (existingPopup) existingPopup.remove();

        const popup = document.createElement('div');
        popup.id = 'free-download-popup';
        const colorScheme = document.getElementById('popup-free-download-config').dataset.colorScheme || '';
        popup.className = 'free-download-popup ' + colorScheme + ' gradient';
        
        popup.innerHTML = `
            <div class="free-download-popup__content">
                <div class="free-download-popup__bg">
                    ${config.coverImg ? `<img class="free-download-popup__image" src="${config.coverImg}" alt="${config.beatTitle}" onload="this.classList.add('loaded')">` : ''}
                </div>
                <button class="free-download-popup__close" type="button">×</button>
                <div class="free-download-popup__fields">
                    <h3 class="popup-title">${config.title}</h3>
                    <p class="popup-subtitle">${config.subtitle}</p>
                    <form class="popup-form" data-audio-url="${config.audioUrl}">
                        <div class="popup-field">
                            <label for="popup-firstname">Prénom</label>
                            <input type="text" id="popup-firstname" name="firstName" required>
                        </div>
                        <div class="popup-field">
                            <label for="popup-lastname">Nom</label>
                            <input type="text" id="popup-lastname" name="lastName" required>
                        </div>
                        <div class="popup-field">
                            <label for="popup-email">Adresse e-mail</label>
                            <input type="email" id="popup-email" name="email" required>
                        </div>
                        <div class="popup-privacy">
                            <input type="checkbox" id="popup-privacy" required>
                            <label for="popup-privacy">J'accepte de recevoir des informations par e-mail</label>
                        </div>
                        <button type="submit" class="popup-submit">${config.buttonText}</button>
                    </form>
                </div>
            </div>
        `;

        document.body.appendChild(popup);
        document.body.style.overflow = 'hidden';

        popup.querySelector('.free-download-popup__close').addEventListener('click', () => this.closeDownloadPopup());
        popup.addEventListener('click', (e) => {
            if (e.target === popup) this.closeDownloadPopup();
        });
        popup.querySelector('.popup-form').addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleDownloadSubmit(e.target);
        });

        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.visibility = 'visible';
        });
    }

    closeDownloadPopup() {
        const popup = document.getElementById('free-download-popup');
        if (popup) {
            popup.style.opacity = '0';
            document.body.style.overflow = '';
            setTimeout(() => popup.remove(), 300);
        }
    }

    async handleDownloadSubmit(form) {
        const fd = new FormData(form);
        const firstName = fd.get('firstName') || '';
        const lastName  = fd.get('lastName') || '';
        const email     = fd.get('email') || '';
        const beatTitle = (document.querySelector('.popup-title')?.innerText) || 'Unknown Beat';
        const audioUrl  = form.dataset.audioUrl || '';

        const payload = {
            beatTitle: beatTitle,
            email: email,
            name: (firstName + ' ' + lastName).trim(),
            lang: navigator.language || navigator.userLanguage || 'en',
            downloadUrl: audioUrl
        };

        const GAS_URL = 'https://script.google.com/macros/s/AKfycbzQ__-H4NHtyNcugr9OlkVIfugiF0L2QFVJ1vUJzIXDeViemKzGRbHq5RMxAcHil0Iv/exec';

        try {
            const iframeName = 'gsPostFrame';
            let iframe = document.querySelector(`iframe[name="${iframeName}"]`);
            if (!iframe) {
                iframe = document.createElement('iframe');
                iframe.name = iframeName;
                iframe.style.display = 'none';
                document.body.appendChild(iframe);
            }

            const postForm = document.createElement('form');
            postForm.method = 'POST';
            postForm.action = GAS_URL;
            postForm.target = iframeName;
            postForm.style.display = 'none';

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'data';
            input.value = JSON.stringify(payload);
            postForm.appendChild(input);

            document.body.appendChild(postForm);
            postForm.submit();

            setTimeout(() => postForm.remove(), 2500);
            this.showNotification("Merci ! Le lien de téléchargement a été envoyé par e-mail.", "success");
        } catch (err) {
            console.error('iframe POST failed, fallback fetch', err);
            try {
                await fetch(GAS_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                    body: new URLSearchParams({ data: JSON.stringify(payload) })
                });
                this.showNotification("Merci ! Le lien de téléchargement a été envoyé par e-mail.", "success");
            } catch (err2) {
                console.error('Fallback fetch failed', err2);
                this.showNotification("Une erreur est survenue. Réessayez plus tard.", "error");
            }
        }

        this.closeDownloadPopup();
    }

    initVolumeControls() {
        if (!this.volumeBtn || !this.volumeSliderTrack) return;

        const volumeControl = this.volumeBtn.closest('.player-volume-control');
        let sliderShowTimeout;

        const preventDefaults = (e) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const clearStuckStates = () => {
            this.volumeBtn.style.transform = '';
            this.volumeBtn.style.backgroundColor = '';
            this.volumeBtn.blur();
            this.volumeBtn.offsetHeight;
        };

        const setVolumeFromEvent = (e) => {
            if (!this.currentAudio) return;
            const trackRect = this.volumeSliderTrack.getBoundingClientRect();
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            let volumePercent = ((trackRect.bottom - clientY) / trackRect.height) * 100;
            volumePercent = Math.max(0, Math.min(100, volumePercent));
            this.setVolume(volumePercent);
        };

        if (!this.isMobileDevice()) {
            this.volumeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                clearStuckStates();
                if (!this.currentAudio) return;
                
                if (this.currentAudio.volume > 0) {
                    this.lastVolume = this.currentAudio.volume * 100;
                    this.setVolume(0);
                } else {
                    const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
                    this.setVolume(volumeToRestore);
                }
            });

            volumeControl.addEventListener('mouseenter', () => {
                clearTimeout(sliderShowTimeout);
                volumeControl.classList.add('show-slider');
            });

            volumeControl.addEventListener('mouseleave', () => {
                sliderShowTimeout = setTimeout(() => {
                    volumeControl.classList.remove('show-slider');
                }, 200);
            });

        } else {
            this.volumeBtn.addEventListener('touchstart', preventDefaults, { passive: false });
            this.volumeBtn.addEventListener('touchend', (e) => {
                preventDefaults(e);
                clearStuckStates();
                
                if (volumeControl.classList.contains('show-slider')) {
                    volumeControl.classList.remove('show-slider');
                } else {
                    volumeControl.classList.add('show-slider');
                }
            }, { passive: false });

            document.addEventListener('touchstart', (e) => {
                if (!volumeControl.contains(e.target)) {
                    volumeControl.classList.remove('show-slider');
                }
            });
        }

        let isDragging = false;

        const startDrag = (e) => {
            isDragging = true;
            this.isDraggingVolume = true;
            setVolumeFromEvent(e);
            if (e.type === 'touchstart') preventDefaults(e);
        };

        const moveDrag = (e) => {
            if (isDragging) {
                setVolumeFromEvent(e);
                if (e.type === 'touchmove') preventDefaults(e);
            }
        };

        const endDrag = () => {
            isDragging = false;
            this.isDraggingVolume = false;
        };

        this.volumeSliderTrack.addEventListener('mousedown', startDrag);
        this.volumeSliderTrack.addEventListener('touchstart', startDrag, { passive: false });
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('touchmove', moveDrag, { passive: false });
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);

        this.setVolume(50);
    }
    
    setVolume(volume) {
        const newVolume = Math.max(0, Math.min(100, volume));
        
        if (newVolume > 0) this.lastVolume = newVolume;

        if (this.volumeSliderFill) {
            const volumeFillColor = getComputedStyle(this.volumeSliderFill).backgroundColor || '#f5deb3';
            this.volumeSliderFill.style.cssText = `
                height: ${newVolume}% !important;
                background-color: ${volumeFillColor} !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                position: absolute !important;
                bottom: 0 !important;
                left: 0 !important;
                width: 100% !important;
                z-index: 2147483647 !important;
            `;
        }

        this.updateVolumeIcon(newVolume);
        if (!this.currentAudio) return;
        this.currentAudio.volume = newVolume / 100;
        this.isMuted = (newVolume === 0);
    }
    
    updateVolumeIcon(volume) {
        if (!this.volumeIcons.normal || !this.volumeIcons.mute) return;
        
        this.volumeIcons.normal.style.display = 'none';
        this.volumeIcons.mute.style.display = 'none';

        if (volume === 0) {
            this.volumeIcons.mute.style.display = 'block';
        } else {
            this.volumeIcons.normal.style.display = 'block';
        }
    }

    initKeyboardControls() {
        document.addEventListener('keydown', (e) => {
            if (this.isInputFocused()) return;
            if (!this.currentAudio) return;

            switch (e.key) {
                case 'ArrowRight':
                    e.preventDefault();
                    this.seekForward();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    this.seekBackward();
                    break;
                case ' ':
                    e.preventDefault();
                    this.togglePlayPause();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    this.playPrevious();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    this.playNext();
                    break;
                case 'n':
                case 'N':
                    e.preventDefault();
                    this.playNext();
                    break;
                case 'p':
                case 'P':
                    e.preventDefault();
                    this.playPrevious();
                    break;
                case 'm':
                case 'M':
                    e.preventDefault();
                    this.toggleMute();
                    break;
            }
        });
    }

    isInputFocused() {
        const activeElement = document.activeElement;
        const tagName = activeElement?.tagName?.toLowerCase();
        return tagName === 'input' || tagName === 'textarea' || activeElement?.isContentEditable;
    }

    seekForward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        this.currentAudio.currentTime = Math.min(this.currentAudio.currentTime + this.seekStep, this.currentAudio.duration);
    }

    seekBackward() {
        if (!this.currentAudio || !this.currentAudio.duration) return;
        this.currentAudio.currentTime = Math.max(this.currentAudio.currentTime - this.seekStep, 0);
    }

    toggleMute() {
        if (!this.currentAudio) return;
        
        if (this.currentAudio.volume > 0) {
            this.lastVolume = this.currentAudio.volume * 100;
            this.setVolume(0);
        } else {
            const volumeToRestore = this.lastVolume > 5 ? this.lastVolume : 50;
            this.setVolume(volumeToRestore);
        }
        this.showVolumeSlider();
    }

    toggleAutoplay() {
        this.autoplayEnabled = !this.autoplayEnabled;
        const status = this.autoplayEnabled ? 'activé' : 'désactivé';
        this.showNotification(`Autoplay ${status}`, 'success');
    }

    showVolumeSlider() {
        const volumeControl = this.volumeBtn?.closest('.player-volume-control');
        if (!volumeControl) return;

        if (this.volumeSliderTimeout) clearTimeout(this.volumeSliderTimeout);
        volumeControl.classList.add('show-slider');

        this.volumeSliderTimeout = setTimeout(() => {
            volumeControl.classList.remove('show-slider');
        }, 1500);
    }

    initScrollVolumeControl() {
        if (!this.playerBar) return;

        this.playerBar.addEventListener('wheel', (e) => {
            if (!this.currentAudio) return;
            e.preventDefault();
            
            const currentVolume = this.currentAudio.volume * 100;
            if (e.deltaY < 0) {
                this.setVolume(Math.min(currentVolume + 5, 100));
            } else {
                this.setVolume(Math.max(currentVolume - 5, 0));
            }
            this.showVolumeSlider();
        }, { passive: false });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.beat-store-section')) {
        new BeatStore();
    }
});